import type { IconName } from '../icons';
import { canViewPath } from '../../services/capabilities';
import type { StringKey } from '../../i18n';

// The single source of truth for primary navigation. Both nav surfaces
// — the desktop top nav and the phone bottom tab bar — read this one list, so a
// route is added once and appears in the right place(s). The two surfaces show
// curated subsets, not the same set: the top nav carries the fuller menu (incl.
// account settings), while the tab bar is the thumb-reachable destinations.
//
// Labels are catalogue KEYS, not literals: the nav renderers resolve them with
// t() at render time (AppNav), so the label follows the active locale even
// though this list is a module-level constant evaluated at import (FE-006).
export interface NavItem {
  href: string;
  labelKey: StringKey; // top-nav / account-menu label key
  tabLabelKey?: StringKey; // tab-bar label key when it differs from labelKey
  icon?: IconName; // glyph for the tab bar and the account menu (required for tab-bar items)
  inTopNav?: boolean; // default true
  inTabBar?: boolean; // default false
  inAccountMenu?: boolean; // default false — desk account-menu route entries (FE-010)
  inAdminMenu?: boolean; // default false — admin section destinations (FE-011; FE-017 section bar)
}

export const NAV_ITEMS: NavItem[] = [
  { href: '/', labelKey: 'nav.home', icon: 'home', inTabBar: true },
  { href: '/work-orders', labelKey: 'nav.workOrders', tabLabelKey: 'nav.workOrders.tab', icon: 'cases', inTabBar: true },
  { href: '/trips', labelKey: 'nav.trips', tabLabelKey: 'nav.trips', icon: 'compass', inTabBar: true },
  { href: '/reports', labelKey: 'nav.reports' },
  // Admin is desk work: ONE "Admin" top-nav entry — a direct link into the admin
  // section (FE-017, amending FE-011's dropdown) — never a flat top-level link
  // (inTopNav:false) and never the thumb-reachable tab bar. This ordered set is
  // the single source for both that link's landing target (adminMenuItems()[0])
  // and the horizontal section bar (AdminRail, FE-017): Roles · Members ·
  // Companies · Subdivisions · … Each entry drops out on its own for a caller
  // without the module capability — including every phone-bound session, since
  // the ceiling composes into it — and the Admin link itself hides when none
  // survive (FE-017.2, regression-guarding FE-011.2).
  { href: '/admin/roles', labelKey: 'common.roles', inTopNav: false, inAdminMenu: true },
  { href: '/admin/users', labelKey: 'common.members', inTopNav: false, inAdminMenu: true },
  { href: '/admin/companies', labelKey: 'common.companies', inTopNav: false, inAdminMenu: true },
  { href: '/admin/subdivisions', labelKey: 'common.subdivisions', inTopNav: false, inAdminMenu: true },
  // The counterparty catalog (issue 544): company master data gated on the
  // `parties` module, sitting between Subdivisions (who hauls) and Locations
  // (where) because it answers "who do we trade with". Drops out for every
  // phone-bound session via the ceiling composed into the module view.
  { href: '/admin/parties', labelKey: 'common.parties', inTopNav: false, inAdminMenu: true },
  // The location manager (FE-027): company master data, gated on the `locations`
  // module (dispatcher Limited, admin/ops Full) — a company-plane entry like
  // Subdivisions, not an HQ lens; drops out for every phone-bound session via
  // the ceiling composed into the module view.
  { href: '/admin/locations', labelKey: 'common.locations', inTopNav: false, inAdminMenu: true },
  // The catalog admin (FE-030): the company's vocabulary + reference catalogs
  // (statuses, reason codes, cargo kinds, cities, synonym maps), gated on the
  // `catalogs` module (D18's home — a company-plane entry, never platform) so
  // a company admin reaches it; drops out for every phone-bound session via
  // the ceiling composed into the module view.
  { href: '/admin/catalogs', labelKey: 'common.catalogs', inTopNav: false, inAdminMenu: true },
  // Company-plane Activity lens (API-007.A): the active company's audit trail +
  // member sign-in feed. It rides the same Admin dropdown/rail but gates on a
  // company-admin module (admin_users via capabilities.ts), NOT the platform
  // module — so any company admin reaches it (unlike the HQ-only Status/Security
  // below), and it still drops out for every phone-bound session on its own.
  { href: '/admin/activity', labelKey: 'common.activity', inTopNav: false, inAdminMenu: true },
  // Platform-plane lenses (API-006): the deployment-wide Status + Security
  // consoles. They ride the same Admin dropdown/rail but gate on the `platform`
  // module, not a company-admin module — so they surface only for the HQ persona
  // (capabilities.ts maps these paths to `platform`, which is None for every
  // role until a company grants it), and drop out for every non-HQ admin and
  // every phone-bound session with no bespoke logic.
  { href: '/admin/status', labelKey: 'common.status', inTopNav: false, inAdminMenu: true },
  { href: '/admin/security', labelKey: 'common.security', inTopNav: false, inAdminMenu: true },
  // The HQ-roster lens (issue 309): who holds standing all-company access — the
  // read half of RBAC-011's legibility goal. Same platform gate as Status/Security.
  { href: '/admin/hq-roster', labelKey: 'common.hqRoster', inTopNav: false, inAdminMenu: true },
  // The Config lens + errlog viewer (API-007.B) ride the same platform gate as
  // Status/Security — HQ-only, desk-only, dropping out for every non-HQ admin and
  // phone-bound session with no bespoke logic (capabilities.ts maps both to
  // `platform`).
  { href: '/admin/config', labelKey: 'common.config', inTopNav: false, inAdminMenu: true },
  { href: '/admin/errors', labelKey: 'common.errors', inTopNav: false, inAdminMenu: true },
  // The fleet lens (FE-032): the GPS capture corpus's admin surface. Same
  // platform gate as Status/Security — HQ-only, desk-only, no bespoke logic.
  { href: '/admin/fleet', labelKey: 'common.fleet', inTopNav: false, inAdminMenu: true },
  { href: '/me', labelKey: 'common.me', icon: 'me', inTopNav: false, inTabBar: true },
  // Account settings: reachable from the desk account menu (FE-010, §5.7) and the
  // phone Me hub — no longer flat top-level links. inTopNav:false drops them from
  // the desktop row; inAccountMenu:true lists them in the account dropdown.
  {
    href: '/settings/devices',
    labelKey: 'common.devicesAndSessions',
    icon: 'devices',
    inTopNav: false,
    inAccountMenu: true,
  },
  {
    href: '/settings/passkeys',
    labelKey: 'common.passkeys',
    icon: 'passkey',
    inTopNav: false,
    inAccountMenu: true,
  },
];

// Prefix-aware active matching so a tab stays lit on its sub-pages — e.g. Me on
// /settings/devices (reached from the Me hub), Work Orders on a future
// /work-orders/:id. '/' matches only itself.
export function isActiveTab(href: string, pathname: string = location.pathname): boolean {
  if (pathname === href) return true;
  if (href === '/') return false;
  if (pathname.startsWith(href + '/')) return true;
  return href === '/me' && pathname.startsWith('/settings/');
}

// The nav surfaces drop any target this user's role + device class can't reach
// (client mirror); open routes (home, account settings) always pass.
export function topNavItems(): NavItem[] {
  return NAV_ITEMS.filter((i) => i.inTopNav !== false && canViewPath(i.href));
}

export function tabBarItems(): NavItem[] {
  return NAV_ITEMS.filter((i) => i.inTabBar === true && canViewPath(i.href));
}

// The admin destinations in canonical order (Roles · Members · Companies ·
// Subdivisions · …) — the single source (FE-011.3/FE-017.3) for the admin
// section bar (AdminRail). Unfiltered: the ORDER + membership. Used only by the
// dev catalog + tests, so it tree-shakes out of the production bundle.
export function adminNavItems(): NavItem[] {
  return NAV_ITEMS.filter((i) => i.inAdminMenu === true);
}

// The admin destinations this caller can actually reach, filtered by the same
// RBAC mirror as every other surface. Drives both the desk Admin top-nav link's
// landing target (adminMenuItems()[0]) and the horizontal section bar (AdminRail)
// — one single source (FE-011.3/FE-017.3); the Admin link renders only when this
// is non-empty, so a capability-less (or phone-bound) session sees no Admin entry
// at all (FE-017.2, regression-guarding FE-011.2). The membership predicate is
// inlined rather than calling adminNavItems() so that helper — used only by the
// dev catalog + tests — tree-shakes out of the production bundle.
export function adminMenuItems(): NavItem[] {
  return NAV_ITEMS.filter((i) => i.inAdminMenu === true && canViewPath(i.href));
}

// isAdminArea names the routes the desk Admin top-nav link owns, so the link can
// light while the user is on any of them — the desk mirror of the account
// trigger's isAccountArea rule, single-sourced here (FE-011.3/FE-017.2).
export function isAdminArea(pathname: string = location.pathname): boolean {
  return pathname.startsWith('/admin/');
}

// The desk account menu's route entries (Devices & sessions, Passkeys), filtered
// by the same RBAC mirror as the other surfaces. The menu's other rows (switch
// company, appearance, language, sign out) are behaviours, not routes, so they
// live in the AccountMenu component rather than here (FE-010.4).
export function accountMenuItems(): NavItem[] {
  return NAV_ITEMS.filter((i) => i.inAccountMenu === true && canViewPath(i.href));
}

// isAccountArea names the routes the desk account-menu trigger owns, so the
// trigger can light while the user is on one of them — the desk mirror of the
// tab bar's /me prefix rule (isActiveTab). Kept here so ordering and active-state
// stay single-sourced in the nav model (FE-010.4).
export function isAccountArea(pathname: string = location.pathname): boolean {
  return pathname === '/me' || pathname.startsWith('/settings/');
}
